/**
 * lobbyProps.ts - Props specific to lobby rooms
 */

export type LobbyPropId =
  | 'sofa'
  | 'tv_screen'
  | 'vending_machine'
  | 'info_sign'
  | 'potted_plant'
  | 'umbrella_stand'
  // ticket_booth props
  | 'candle'
  | 'cobweb'
  | 'pumpkin'
  // waiting_lounge props
  | 'magazine_rack'
  // vip_lounge props
  | 'chandelier'
  | 'wine_glass'
  | 'velvet_curtain'
  | 'gold_frame'
  // poster_wall props
  | 'picture_frame'
  | 'spotlight'
  | 'skull_ornament'
  // snack_cart props
  | 'pumpkin_cookie'
  | 'ghost_drink'
  | 'napkin_holder'
  // souvenir_booth props
  | 'display_shelf'
  | 'plush_doll'
  | 'keychain_rack'
  | 'wrapping_paper'
  // photo_zone props
  | 'backdrop'
  | 'prop_box'
  | 'mask_stand'
  // tv_monitor props
  | 'antenna'
  | 'remote_control'
  | 'speaker'
  | 'cable_wires'
  // costume_rental props
  | 'clothes_rack'
  | 'mirror'
  | 'makeup_table'
  | 'wig_stand'
  // fortune_booth props
  | 'crystal_ball'
  | 'tarot_cards'
  | 'curtain'
  | 'candelabra';

export interface LobbyPropDef {
  id: LobbyPropId;
  nameKey: string;
  descKey: string;
  maxLevel: number;
  baseCost: number;
  costMultiplier: number;
  /** Bonus effect (e.g. patience increase per level) */
  bonusPct: number;
}

export const LOBBY_PROP_DEFS: Record<LobbyPropId, LobbyPropDef> = {
  sofa: {
    id: 'sofa',
    nameKey: 'lobby_prop_sofa',
    descKey: 'lobby_prop_sofa_desc',
    maxLevel: 3,
    baseCost: 100,
    costMultiplier: 2.0,
    bonusPct: 0.05,
  },
  tv_screen: {
    id: 'tv_screen',
    nameKey: 'lobby_prop_tv_screen',
    descKey: 'lobby_prop_tv_screen_desc',
    maxLevel: 3,
    baseCost: 200,
    costMultiplier: 2.5,
    bonusPct: 0.08,
  },
  vending_machine: {
    id: 'vending_machine',
    nameKey: 'lobby_prop_vending_machine',
    descKey: 'lobby_prop_vending_machine_desc',
    maxLevel: 3,
    baseCost: 150,
    costMultiplier: 2.0,
    bonusPct: 0.06,
  },
  info_sign: {
    id: 'info_sign',
    nameKey: 'lobby_prop_info_sign',
    descKey: 'lobby_prop_info_sign_desc',
    maxLevel: 3,
    baseCost: 80,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },
  potted_plant: {
    id: 'potted_plant',
    nameKey: 'lobby_prop_potted_plant',
    descKey: 'lobby_prop_potted_plant_desc',
    maxLevel: 3,
    baseCost: 60,
    costMultiplier: 2.0,
    bonusPct: 0.03,
  },
  umbrella_stand: {
    id: 'umbrella_stand',
    nameKey: 'lobby_prop_umbrella_stand',
    descKey: 'lobby_prop_umbrella_stand_desc',
    maxLevel: 3,
    baseCost: 50,
    costMultiplier: 2.0,
    bonusPct: 0.03,
  },

  // ticket_booth props
  candle: {
    id: 'candle',
    nameKey: 'lobby_prop_candle',
    descKey: 'lobby_prop_candle_desc',
    maxLevel: 3,
    baseCost: 40,
    costMultiplier: 2.0,
    bonusPct: 0.03,
  },
  cobweb: {
    id: 'cobweb',
    nameKey: 'lobby_prop_cobweb',
    descKey: 'lobby_prop_cobweb_desc',
    maxLevel: 3,
    baseCost: 30,
    costMultiplier: 2.0,
    bonusPct: 0.02,
  },
  pumpkin: {
    id: 'pumpkin',
    nameKey: 'lobby_prop_pumpkin',
    descKey: 'lobby_prop_pumpkin_desc',
    maxLevel: 3,
    baseCost: 60,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },

  // waiting_lounge props
  magazine_rack: {
    id: 'magazine_rack',
    nameKey: 'lobby_prop_magazine_rack',
    descKey: 'lobby_prop_magazine_rack_desc',
    maxLevel: 3,
    baseCost: 70,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },

  // vip_lounge props
  chandelier: {
    id: 'chandelier',
    nameKey: 'lobby_prop_chandelier',
    descKey: 'lobby_prop_chandelier_desc',
    maxLevel: 3,
    baseCost: 300,
    costMultiplier: 2.5,
    bonusPct: 0.08,
  },
  wine_glass: {
    id: 'wine_glass',
    nameKey: 'lobby_prop_wine_glass',
    descKey: 'lobby_prop_wine_glass_desc',
    maxLevel: 3,
    baseCost: 150,
    costMultiplier: 2.0,
    bonusPct: 0.05,
  },
  velvet_curtain: {
    id: 'velvet_curtain',
    nameKey: 'lobby_prop_velvet_curtain',
    descKey: 'lobby_prop_velvet_curtain_desc',
    maxLevel: 3,
    baseCost: 200,
    costMultiplier: 2.5,
    bonusPct: 0.06,
  },
  gold_frame: {
    id: 'gold_frame',
    nameKey: 'lobby_prop_gold_frame',
    descKey: 'lobby_prop_gold_frame_desc',
    maxLevel: 3,
    baseCost: 250,
    costMultiplier: 2.5,
    bonusPct: 0.07,
  },

  // poster_wall props
  picture_frame: {
    id: 'picture_frame',
    nameKey: 'lobby_prop_picture_frame',
    descKey: 'lobby_prop_picture_frame_desc',
    maxLevel: 3,
    baseCost: 80,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },
  spotlight: {
    id: 'spotlight',
    nameKey: 'lobby_prop_spotlight',
    descKey: 'lobby_prop_spotlight_desc',
    maxLevel: 3,
    baseCost: 120,
    costMultiplier: 2.0,
    bonusPct: 0.05,
  },
  skull_ornament: {
    id: 'skull_ornament',
    nameKey: 'lobby_prop_skull_ornament',
    descKey: 'lobby_prop_skull_ornament_desc',
    maxLevel: 3,
    baseCost: 90,
    costMultiplier: 2.0,
    bonusPct: 0.05,
  },

  // snack_cart props
  pumpkin_cookie: {
    id: 'pumpkin_cookie',
    nameKey: 'lobby_prop_pumpkin_cookie',
    descKey: 'lobby_prop_pumpkin_cookie_desc',
    maxLevel: 3,
    baseCost: 50,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },
  ghost_drink: {
    id: 'ghost_drink',
    nameKey: 'lobby_prop_ghost_drink',
    descKey: 'lobby_prop_ghost_drink_desc',
    maxLevel: 3,
    baseCost: 60,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },
  napkin_holder: {
    id: 'napkin_holder',
    nameKey: 'lobby_prop_napkin_holder',
    descKey: 'lobby_prop_napkin_holder_desc',
    maxLevel: 3,
    baseCost: 30,
    costMultiplier: 2.0,
    bonusPct: 0.02,
  },

  // souvenir_booth props
  display_shelf: {
    id: 'display_shelf',
    nameKey: 'lobby_prop_display_shelf',
    descKey: 'lobby_prop_display_shelf_desc',
    maxLevel: 3,
    baseCost: 100,
    costMultiplier: 2.0,
    bonusPct: 0.05,
  },
  plush_doll: {
    id: 'plush_doll',
    nameKey: 'lobby_prop_plush_doll',
    descKey: 'lobby_prop_plush_doll_desc',
    maxLevel: 3,
    baseCost: 80,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },
  keychain_rack: {
    id: 'keychain_rack',
    nameKey: 'lobby_prop_keychain_rack',
    descKey: 'lobby_prop_keychain_rack_desc',
    maxLevel: 3,
    baseCost: 60,
    costMultiplier: 2.0,
    bonusPct: 0.03,
  },
  wrapping_paper: {
    id: 'wrapping_paper',
    nameKey: 'lobby_prop_wrapping_paper',
    descKey: 'lobby_prop_wrapping_paper_desc',
    maxLevel: 3,
    baseCost: 40,
    costMultiplier: 2.0,
    bonusPct: 0.02,
  },

  // photo_zone props
  backdrop: {
    id: 'backdrop',
    nameKey: 'lobby_prop_backdrop',
    descKey: 'lobby_prop_backdrop_desc',
    maxLevel: 3,
    baseCost: 150,
    costMultiplier: 2.5,
    bonusPct: 0.06,
  },
  prop_box: {
    id: 'prop_box',
    nameKey: 'lobby_prop_prop_box',
    descKey: 'lobby_prop_prop_box_desc',
    maxLevel: 3,
    baseCost: 100,
    costMultiplier: 2.0,
    bonusPct: 0.05,
  },
  mask_stand: {
    id: 'mask_stand',
    nameKey: 'lobby_prop_mask_stand',
    descKey: 'lobby_prop_mask_stand_desc',
    maxLevel: 3,
    baseCost: 80,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },

  // tv_monitor props
  antenna: {
    id: 'antenna',
    nameKey: 'lobby_prop_antenna',
    descKey: 'lobby_prop_antenna_desc',
    maxLevel: 3,
    baseCost: 50,
    costMultiplier: 2.0,
    bonusPct: 0.03,
  },
  remote_control: {
    id: 'remote_control',
    nameKey: 'lobby_prop_remote_control',
    descKey: 'lobby_prop_remote_control_desc',
    maxLevel: 3,
    baseCost: 30,
    costMultiplier: 2.0,
    bonusPct: 0.02,
  },
  speaker: {
    id: 'speaker',
    nameKey: 'lobby_prop_speaker',
    descKey: 'lobby_prop_speaker_desc',
    maxLevel: 3,
    baseCost: 80,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },
  cable_wires: {
    id: 'cable_wires',
    nameKey: 'lobby_prop_cable_wires',
    descKey: 'lobby_prop_cable_wires_desc',
    maxLevel: 3,
    baseCost: 20,
    costMultiplier: 2.0,
    bonusPct: 0.02,
  },

  // costume_rental props
  clothes_rack: {
    id: 'clothes_rack',
    nameKey: 'lobby_prop_clothes_rack',
    descKey: 'lobby_prop_clothes_rack_desc',
    maxLevel: 3,
    baseCost: 100,
    costMultiplier: 2.0,
    bonusPct: 0.05,
  },
  mirror: {
    id: 'mirror',
    nameKey: 'lobby_prop_mirror',
    descKey: 'lobby_prop_mirror_desc',
    maxLevel: 3,
    baseCost: 120,
    costMultiplier: 2.0,
    bonusPct: 0.05,
  },
  makeup_table: {
    id: 'makeup_table',
    nameKey: 'lobby_prop_makeup_table',
    descKey: 'lobby_prop_makeup_table_desc',
    maxLevel: 3,
    baseCost: 150,
    costMultiplier: 2.5,
    bonusPct: 0.06,
  },
  wig_stand: {
    id: 'wig_stand',
    nameKey: 'lobby_prop_wig_stand',
    descKey: 'lobby_prop_wig_stand_desc',
    maxLevel: 3,
    baseCost: 70,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },

  // fortune_booth props
  crystal_ball: {
    id: 'crystal_ball',
    nameKey: 'lobby_prop_crystal_ball',
    descKey: 'lobby_prop_crystal_ball_desc',
    maxLevel: 3,
    baseCost: 200,
    costMultiplier: 2.5,
    bonusPct: 0.07,
  },
  tarot_cards: {
    id: 'tarot_cards',
    nameKey: 'lobby_prop_tarot_cards',
    descKey: 'lobby_prop_tarot_cards_desc',
    maxLevel: 3,
    baseCost: 100,
    costMultiplier: 2.0,
    bonusPct: 0.05,
  },
  curtain: {
    id: 'curtain',
    nameKey: 'lobby_prop_curtain',
    descKey: 'lobby_prop_curtain_desc',
    maxLevel: 3,
    baseCost: 80,
    costMultiplier: 2.0,
    bonusPct: 0.04,
  },
  candelabra: {
    id: 'candelabra',
    nameKey: 'lobby_prop_candelabra',
    descKey: 'lobby_prop_candelabra_desc',
    maxLevel: 3,
    baseCost: 130,
    costMultiplier: 2.0,
    bonusPct: 0.06,
  },
};

export const ALL_LOBBY_PROP_IDS = Object.keys(LOBBY_PROP_DEFS) as LobbyPropId[];

export function lobbyPropCostForLevel(propId: LobbyPropId, level: number): number {
  const def = LOBBY_PROP_DEFS[propId];
  if (!def) return 0;
  return Math.floor(def.baseCost * Math.pow(def.costMultiplier, level - 1));
}
